/* eslint-disable no-sequences,@typescript-eslint/no-unused-expressions */
//@ts-nocheck
import { tickFormat } from "d3-scale";
import { ticks, tickIncrement } from "d3-array";
import {interpolate as interpolateValue, interpolateNumber, interpolateRound} from "d3-interpolate";

// from d3-scale/constant
function constant(x) {
    return function() { return x; }
}

// from d3-scale/init
function initRange(domain, range) {
    switch (arguments.length) {
        case 0: break;
        case 1: this.range(domain); break;
        default: this.range(range).domain(domain); break;
    }
    return this;
}

// from d3-scale/number
function number(x) {
    return +x;
}

// from d3-scale/continuous
const unit = [0, 1];

function identity(x) {
    return x;
}

function normalize(a, b) {
    // eslint-disable-next-line no-cond-assign
    return (b -= (a = +a))
        ? function(x) { return (x - a) / b; }
        : constant(isNaN(b) ? NaN : 0.5);
}

function clamper(a, b) {
    let t;
    if (a > b) t = a, a = b, b = t;
    return function(x) { return Math.max(a, Math.min(b, x)); };
}

function bimap(domain, range, interpolate) {
    let d0 = domain[0];
    const d1 = domain[1];
    let r0 = range[0];
    const r1 = range[1];
    if (d1 < d0) d0 = normalize(d1, d0), r0 = interpolate(r1, r0);
    else d0 = normalize(d0, d1), r0 = interpolate(r0, r1);
    return function(x) { return r0(d0(x)); };
}

function copy(source, target) {
    return target
        .domain(source.domain())
        .range(source.range())
        .interpolate(source.interpolate())
        .clamp(source.clamp())
        .unknown(source.unknown());
}

function transformer() {
    let domain = unit,
        range = unit,
        interpolate = interpolateValue,
        transform,
        untransform,
        unknown,
        clamp = identity,
        piecewise,
        output,
        input;

    function rescale() {
        const n = Math.min(domain.length, range.length);
        if (clamp !== identity) clamp = clamper(domain[0], domain[n - 1]);
        piecewise = n > 2 ? polymap : bimap;
        output = input = null;
        return scale;
    }

    function scale(x) {
        return isNaN(x = +x) ? unknown : (output || (output = piecewise(domain.map(transform), range, interpolate)))(transform(clamp(x)));
    }

    scale.invert = function(y) {
        return clamp(untransform((input || (input = piecewise(range, domain.map(transform), interpolateNumber)))(y)));
    };

    scale.domain = function(_) {
        return arguments.length ? (domain = Array.from(_, number), rescale()) : domain.slice();
    };

    scale.range = function(_) {
        return arguments.length ? (range = Array.from(_), rescale()) : range.slice();
    };

    scale.rangeRound = function(_) {
        return range = Array.from(_), interpolate = interpolateRound, rescale();
    };

    scale.clamp = function(_) {
        return arguments.length ? (clamp = _ ? true : identity, rescale()) : clamp !== identity;
    };

    scale.interpolate = function(_) {
        return arguments.length ? (interpolate = _, rescale()) : interpolate;
    };

    scale.unknown = function(_) {
        return arguments.length ? (unknown = _, scale) : unknown;
    };

    return function(t, u) {
        transform = t, untransform = u;
        return rescale();
    };
}

// from d3-scale/linear
function linearish(scale) {
    let domain = scale.domain;

    scale.ticks = function(count) {
        const d = domain();
        return ticks(d[0], d[d.length - 1], count == null ? 10 : count);
    };

    scale.tickFormat = function(count, specifier) {
        const d = domain();
        return tickFormat(d[0], d[d.length - 1], count == null ? 10 : count, specifier);
    };

    scale.nice = function(count) {
        if (count == null) count = 10;

        const d = domain();
        let i0 = 0;
        let i1 = d.length - 1;
        let start = d[i0];
        let stop = d[i1];
        let prestep;
        let step;
        let maxIter = 10;

        if (stop < start) {
            step = start, start = stop, stop = step;
            step = i0, i0 = i1, i1 = step;
        }

        while (maxIter-- > 0) {
            step = tickIncrement(start, stop, count);
            if (step === prestep) {
                d[i0] = start
                d[i1] = stop
                return domain(d);
            } else if (step > 0) {
                start = Math.floor(start / step) * step;
                stop = Math.ceil(stop / step) * step;
            } else if (step < 0) {
                start = Math.ceil(start * step) / step;
                stop = Math.floor(stop * step) / step;
            } else {
                break;
            }
            prestep = step;
        }

        return scale;
    };

    return scale;
}

function transformPowTwenty(x: number) {
    return Math.pow(10, x / 20)
}

function invTransformPowTwenty(x: number) {
    return Math.log10(x) * 20;
}

function powish(transform) {
    let scale = transform(transformPowTwenty, invTransformPowTwenty);

    return linearish(scale);
}

export default function pow(domain: d3.NumberValue[], range: readonly number[]): d3.ScalePower<number, number>{
    const scale = powish(transformer());

    scale.copy = function() {
        return copy(scale, pow());
    };

    initRange.apply(scale, arguments);

    return scale;
}